/**
 * useStockfishEngine
 *
 * Runs Stockfish 10 locally in a Web Worker (public/stockfish.js) via the UCI
 * protocol.  Fully browser-local – no network calls, no latency.
 *
 * Performance design
 * ──────────────────
 * The engine emits an `info depth …` line for every (depth × multipv)
 * combination — up to 66 messages for depth-22 / 3-line analysis.
 * Calling setState on every message would trigger dozens of re-renders per
 * second on the large ChequeredChess component (causing the "screen flash").
 *
 * Fix: all incoming lines are stored in mutable refs; React state is flushed
 * at most once every FLUSH_MS (150 ms), and immediately on `bestmove`.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { Chess } from 'chess.js';

const FLUSH_MS = 150; // maximum React-state update frequency while analysing

export interface PVLine {
    multipv: number;
    depth: number;
    evaluation: number;
    isMate: boolean;
    mateIn?: number;
    bestMoveSan: string;
    bestMoveUci: string;
    pvSan: string[];
    pvUci: string[];
    from: string;
    to: string;
}

export interface StockfishEngineState {
    ready: boolean;
    analyzing: boolean;
    depth: number;
    lines: PVLine[];
    multiPV: number;
    setMultiPV: (n: number) => void;
    threads: number;
    setThreads: (n: number) => void;
    startAnalysis: (fen: string) => void;
    stopAnalysis: () => void;
    analysisFen: string;
}

function parseInfoLine(line: string, fen: string): PVLine | null {
    if (!line.startsWith('info')) return null;

    const tok = (key: string) => line.match(new RegExp(`\\b${key}\\s+(\\S+)`))?.[1];
    const depthStr = tok('depth');
    if (!depthStr) return null;

    const depth    = parseInt(depthStr, 10);
    const multipv  = parseInt(tok('multipv') ?? '1', 10);
    const cpStr    = tok('cp');
    const mateStr  = tok('mate');
    const pvMatch  = line.match(/\bpv\s+(.+)$/);

    let evaluation = 0, isMate = false, mateIn: number | undefined;
    if (mateStr !== undefined) {
        isMate = true; mateIn = parseInt(mateStr, 10);
        evaluation = mateIn > 0 ? 999 : -999;
    } else if (cpStr !== undefined) {
        evaluation = parseInt(cpStr, 10) / 100;
    } else {
        return null;
    }

    const pvUci = pvMatch ? pvMatch[1].trim().split(/\s+/).slice(0, 8) : [];
    const pvSan: string[] = [];
    let bestMoveSan = '', bestMoveUci = '', from = '', to = '';

    try {
        const g = new Chess(fen);
        for (const uci of pvUci) {
            const mv = g.move({ from: uci.slice(0, 2), to: uci.slice(2, 4), promotion: uci.slice(4) || undefined });
            if (!mv) break;
            pvSan.push(mv.san);
            if (pvSan.length === 1) {
                bestMoveSan = mv.san; bestMoveUci = uci;
                from = uci.slice(0, 2); to = uci.slice(2, 4);
            }
        }
    } catch { /* ignore */ }

    if (!bestMoveUci) return null;
    return { multipv, depth, evaluation, isMate, mateIn, bestMoveSan, bestMoveUci, pvSan, pvUci, from, to };
}

export function useStockfishEngine(): StockfishEngineState {
    const workerRef     = useRef<Worker | null>(null);
    const [ready,       setReady]       = useState(false);
    const [analyzing,   setAnalyzing]   = useState(false);
    const [depth,       setDepth]       = useState(0);
    const [lines,       setLines]       = useState<PVLine[]>([]);
    const [multiPV,     setMultiPVState] = useState(3);
    const [threads,     setThreadsState] = useState(navigator.hardwareConcurrency > 1 ? 2 : 1);
    const [analysisFen, setAnalysisFen] = useState('');

    // Refs that receive every engine message without triggering React renders
    const analysisFenRef  = useRef('');
    const multiPVRef      = useRef(multiPV);
    const threadsRef      = useRef(threads);
    const pendingLines    = useRef<Map<number, PVLine>>(new Map()); // keyed by multipv
    const pendingDepth    = useRef(0);
    const flushTimer      = useRef<ReturnType<typeof setTimeout> | null>(null);

    /** Push accumulated lines/depth to React state (called at most every FLUSH_MS). */
    const flush = useCallback((finalFlush = false) => {
        if (flushTimer.current) {
            clearTimeout(flushTimer.current);
            flushTimer.current = null;
        }
        const map = pendingLines.current;
        if (map.size === 0 && !finalFlush) return;
        const sorted = Array.from(map.values()).sort((a, b) => a.multipv - b.multipv);
        setLines(sorted);
        setDepth(pendingDepth.current);
    }, []);

    /** Schedule a flush unless one is already pending. */
    const scheduleFlush = useCallback(() => {
        if (flushTimer.current) return;
        flushTimer.current = setTimeout(() => {
            flushTimer.current = null;
            flush();
        }, FLUSH_MS);
    }, [flush]);

    // Boot the worker once
    useEffect(() => {
        const worker = new Worker('/stockfish.js');
        workerRef.current = worker;

        worker.onmessage = (e: MessageEvent<string>) => {
            const line = typeof e.data === 'string' ? e.data : String(e.data);

            if (line === 'uciok') {
                worker.postMessage(`setoption name MultiPV value ${multiPVRef.current}`);
                worker.postMessage(`setoption name Threads value ${threadsRef.current}`);
                worker.postMessage('isready');
            } else if (line === 'readyok') {
                setReady(true);
            } else if (line.startsWith('info depth')) {
                // ── hot path: write to refs only, schedule a batched flush ──
                const parsed = parseInfoLine(line, analysisFenRef.current);
                if (!parsed) return;
                pendingLines.current.set(parsed.multipv, parsed);
                pendingDepth.current = parsed.depth;
                scheduleFlush();
            } else if (line.startsWith('bestmove')) {
                // Analysis finished — flush immediately, then mark done
                flush(true);
                setAnalyzing(false);
            }
        };

        worker.postMessage('uci');

        return () => {
            if (flushTimer.current) clearTimeout(flushTimer.current);
            worker.postMessage('quit');
            worker.terminate();
        };
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const setMultiPV = useCallback((n: number) => {
        multiPVRef.current = n;
        setMultiPVState(n);
        workerRef.current?.postMessage(`setoption name MultiPV value ${n}`);
        workerRef.current?.postMessage('isready');
    }, []);

    const setThreads = useCallback((n: number) => {
        threadsRef.current = n;
        setThreadsState(n);
        workerRef.current?.postMessage(`setoption name Threads value ${n}`);
        workerRef.current?.postMessage('isready');
    }, []);

    const startAnalysis = useCallback((fen: string) => {
        const w = workerRef.current;
        if (!w) return;
        // Reset buffered state
        pendingLines.current.clear();
        pendingDepth.current = 0;
        if (flushTimer.current) { clearTimeout(flushTimer.current); flushTimer.current = null; }

        analysisFenRef.current = fen;
        setAnalysisFen(fen);
        setLines([]);
        setDepth(0);
        setAnalyzing(true);
        w.postMessage('stop');
        w.postMessage(`setoption name MultiPV value ${multiPVRef.current}`);
        w.postMessage(`position fen ${fen}`);
        w.postMessage('go depth 22');
    }, []);

    const stopAnalysis = useCallback(() => {
        workerRef.current?.postMessage('stop');
        flush(true);
        setAnalyzing(false);
    }, [flush]);

    return { ready, analyzing, depth, lines, multiPV, setMultiPV, threads, setThreads, startAnalysis, stopAnalysis, analysisFen };
}
